import { Row, Col } from '@trybeapp/ui.grid'
import { Box } from '@trybeapp/ui.box'
import { Text } from 'components/Text'
import { DateTime } from 'luxon'
import styled, { css } from '@xstyled/styled-components'
import { difference, first, isEmpty, map } from 'lodash'
import pluralize from 'pluralize'
import { withSlotsByHour, withAppointmentSlotForm, useAppointmentForm } from './contexts'
import { Label } from '@trybeapp/ui.label'
import { Button } from '@trybeapp/ui.button'
import { Money } from '@trybeapp/ui.money'
import { PractitionerProvider, usePractitioner } from 'contexts/Practitioner'
import { Hint } from 'ui/components/Hint'
import { Alert } from '@trybeapp/ui.alert'
import { selectPractitionersByIds } from 'selectors/PractitionerSelectors'
import { useSelector } from 'react-redux'
import {
  EVENT_APPOINTMENT_SLOT_SELECTED,
  EVENT_APPOINTMENT_SLOT_PRACTITIONER_AUTOSUGGESTED,
  EVENT_APPOINTMENT_SLOT_ROOM_AUTOSUGGESTED,
} from 'constants/trackableEvents'
import { useOrderId } from 'screens/OrderDetails/components/Overview/hooks'
import { trackEvent } from 'components/Segment'
import { useMemo } from 'react'
import { useAppointmentType } from 'contexts/AppointmentType'
import {
  useCombinationForPractitionerId,
  useCombinationForRoomId,
  useCurrentPractitionerIds,
  useCurrentRoomId,
  useCurrentEquipmentIds,
  useCurrentSlot,
  useCurrentTime,
  useInvalidTime,
  useSlotsByHour,
} from './hooks'
import { useGetRoom } from 'api/Rooms'
import { useEquipment } from 'api/Equipment'

export const AppointmentSlotSelection = withAppointmentSlotForm(
  withSlotsByHour(({ includePrice }) => {
    const invalidTime = useInvalidTime()

    return (
      <>
        {invalidTime ? (
          <Alert variant="warning" fontSize="body2">
            You have selected an invalid time.
            <Text color="inherit" fontSize="body3">
              Use the form on the left hand side to manually configure this appointment.
            </Text>
          </Alert>
        ) : (
          <>
            <TimeSelection includePrice={includePrice} />
            <PractitionerSelection />
            <RoomSelection />
            <EquipmentSelection />
          </>
        )}
      </>
    )
  })
)

const TimeSelection = ({ includePrice }) => {
  const currentTime = useCurrentTime()
  const slots = useSlotsByHour()
  const { onTimeChange, onPractitionerIdsChange, onRoomIdChange } = useAppointmentForm()

  const handleClear = () => {
    onTimeChange('')
    onPractitionerIdsChange([])
    onRoomIdChange('')
  }

  if (currentTime) {
    return (
      <Box mb="md">
        <Label>Select a time slot</Label>
        <CondensedRow alignItems="center">
          <CondensedCol col="auto" px="sm">
            <SlotPill selected={true}>{currentTime}</SlotPill>
          </CondensedCol>
          <CondensedCol px="sm">
            <Button variant="ghost" onClick={handleClear} size="sm">
              Clear
            </Button>
          </CondensedCol>
        </CondensedRow>
      </Box>
    )
  }

  return (
    <>
      <Box mb="sm">
        <Label>Select a time slot</Label>
        {Object.keys(slots).map((hour) => (
          <HourRow key={hour} hour={hour} includePrice={includePrice} />
        ))}
      </Box>
    </>
  )
}

const PractitionerSelection = () => {
  const currentSlot = useCurrentSlot()
  const { numPractitioners } = useAppointmentForm()
  const { practitioner_ids: validPractitionerIds = [] } = currentSlot
  const selectedPractitionerIds = useCurrentPractitionerIds()
  const practitionerIds = [...new Set([...validPractitionerIds, ...selectedPractitionerIds])]

  if (isEmpty(currentSlot)) return null

  const numPractitionersIsMet = practitionerIds.length >= numPractitioners

  return (
    <Box mb="md">
      <Label>
        Select {numPractitioners === 1 ? 'a' : numPractitioners}{' '}
        {pluralize('practitioner', numPractitioners)}
      </Label>
      <InvalidPractitionerAlert />
      <CondensedRow>
        {numPractitionersIsMet &&
          practitionerIds.map((practitionerId) => (
            <PractitionerProvider id={practitionerId} key={practitionerId}>
              <CondensedCol col={1 / 4}>
                <PractitionerPill />
              </CondensedCol>
            </PractitionerProvider>
          ))}
        {!numPractitionersIsMet && (
          <CondensedCol>
            <div className="mb-4">
              <Hint>
                {numPractitioners === 1 ? 'No' : 'Not enough'} practitioners found for this time
                slot.
              </Hint>
            </div>
          </CondensedCol>
        )}
      </CondensedRow>
      <PractitionerSuggestion />
    </Box>
  )
}

const RoomSelection = () => {
  const currentSlot = useCurrentSlot()
  const { room_ids: roomIds = [] } = currentSlot
  if (isEmpty(currentSlot)) return null

  return (
    <Box mb="md">
      <Label>Select a room</Label>
      <InvalidRoomAlert />
      <CondensedRow>
        {roomIds.map((roomId) => (
          <CondensedCol col={1 / 4} key={roomId}>
            <RoomPill roomId={roomId} />
          </CondensedCol>
        ))}
      </CondensedRow>
      <RoomSuggestion />
    </Box>
  )
}

const EquipmentSelection = () => {
  const currentSlot = useCurrentSlot()
  const { equipment_ids: equipmentIds = [] } = currentSlot

  if (isEmpty(currentSlot) || equipmentIds.length === 0) return null

  return (
    <Box mb="md">
      <Label>Select equipment</Label>
      <CondensedRow>
        {equipmentIds.map((equipmentId) => (
          <CondensedCol col={1 / 4} key={equipmentId}>
            <EquipmentPill equipmentId={equipmentId} />
          </CondensedCol>
        ))}
      </CondensedRow>
      <RoomSuggestion />
    </Box>
  )
}

const RoomSuggestion = () => {
  const currentRoomId = useCurrentRoomId()
  const currentPractitionerIds = useCurrentPractitionerIds()
  const { room_id: suggestedRoomId = '' } =
    useCombinationForPractitionerId(currentPractitionerIds) || {}
  const { data: { data: { name = '' } = {} } = {} } = useGetRoom(suggestedRoomId)

  const { onRoomIdChange } = useAppointmentForm()

  const handleChange = () => {
    onRoomIdChange(suggestedRoomId)
  }

  if (!currentRoomId || !suggestedRoomId) return null
  if (suggestedRoomId === currentRoomId) return null

  return (
    <Box my="sm">
      <Hint>
        The suggested room for the practitioner you have selected is
        <Button
          size="xs"
          variant="primary-info"
          onClick={handleChange}
          display="inline-flex"
          mx="xs"
        >
          {name}
        </Button>
      </Hint>
    </Box>
  )
}

const PractitionerSuggestion = () => {
  const currentRoomId = useCurrentRoomId()
  const currentPractitionerIds = useCurrentPractitionerIds()
  const { practitioner_ids: suggestedPractitionerIds = [] } =
    useCombinationForRoomId(currentRoomId) || {}
  const suggestedPractitioners =
    useSelector(selectPractitionersByIds(suggestedPractitionerIds)) || {}
  const { numPractitioners } = useAppointmentForm()

  const { onPractitionerIdsChange } = useAppointmentForm()

  const handleChange = () => {
    onPractitionerIdsChange(suggestedPractitionerIds)
  }

  if (!currentPractitionerIds || !suggestedPractitionerIds) return null
  if (difference(suggestedPractitionerIds, currentPractitionerIds).length === 0) return null

  return (
    <Box my="sm">
      <Hint>
        The suggested {pluralize('practitioner', numPractitioners)} for the room you have selected{' '}
        {pluralize('is', numPractitioners)}
        <Button
          size="xs"
          variant="primary-info"
          onClick={handleChange}
          display="inline-flex"
          mx="xs"
        >
          {map(suggestedPractitioners, 'name').join(', ')}
        </Button>
      </Hint>
    </Box>
  )
}

const PractitionerPill = () => {
  const currentSlot = useCurrentSlot()
  const currentPractitionerIds = useCurrentPractitionerIds()
  const currentRoomId = useCurrentRoomId()
  const { name, id: practitionerId } = usePractitioner()
  const { numPractitioners } = useAppointmentForm()

  const combinationForPractitioner = useCombinationForPractitionerId(practitionerId)
  const orderId = useOrderId()

  const { onPractitionerIdsChange, onRoomIdChange } = useAppointmentForm()

  const handleSlotClick = () => {
    let newPractitionerIds

    if (currentPractitionerIds.indexOf(practitionerId) >= 0) {
      newPractitionerIds = currentPractitionerIds.filter((id) => id !== practitionerId)
    } else {
      // Only take up to the number of practitioners by slicing off any
      // extras from the start of the array
      newPractitionerIds = [...currentPractitionerIds, practitionerId].slice(numPractitioners * -1)
    }

    onPractitionerIdsChange(newPractitionerIds)

    if (practitionerId !== currentPractitionerIds && !currentRoomId && combinationForPractitioner) {
      onRoomIdChange(combinationForPractitioner.room_id)
      trackEvent(EVENT_APPOINTMENT_SLOT_ROOM_AUTOSUGGESTED, { orderId })
    }
  }

  const isSelected = currentPractitionerIds.indexOf(practitionerId) >= 0

  return (
    <SlotPill
      subtle={(currentSlot.nonoptimal_practitioner_ids ?? []).indexOf(practitionerId) >= 0 ? 1 : 0}
      selected={isSelected}
      onClick={handleSlotClick}
      disabled={
        !isSelected && numPractitioners > 1 && currentPractitionerIds.length >= numPractitioners
      }
    >
      {name}
    </SlotPill>
  )
}

const RoomPill = ({ roomId }) => {
  const currentPractitionerIds = useCurrentPractitionerIds()
  const currentRoomId = useCurrentRoomId()
  const { data: { data: { name = '' } = {} } = {} } = useGetRoom(roomId)
  const combinationForRoom = useCombinationForRoomId(roomId)
  const orderId = useOrderId()

  const { onPractitionerIdsChange, onRoomIdChange } = useAppointmentForm()

  const handleSlotClick = () => {
    onRoomIdChange(roomId === currentRoomId ? '' : roomId)

    if (roomId !== currentRoomId && !currentPractitionerIds && combinationForRoom) {
      onPractitionerIdsChange(combinationForRoom.practitioner_id)
      trackEvent(EVENT_APPOINTMENT_SLOT_PRACTITIONER_AUTOSUGGESTED, { orderId })
    }
  }

  return (
    <SlotPill selected={roomId === currentRoomId} onClick={handleSlotClick}>
      {name}
    </SlotPill>
  )
}

const EquipmentPill = ({ equipmentId }) => {
  const currentEquipmentIds = useCurrentEquipmentIds()
  const { onEquipmentIdsChange } = useAppointmentForm()
  const { data: { data: allEquipment = [] } = {} } = useEquipment({
    perPage: -1,
  })

  const equipment = useMemo(() => {
    return allEquipment.find((equipment) => equipment.id === equipmentId) ?? {}
  }, [equipmentId, allEquipment])

  const handleSlotClick = () => {
    const equipmentIds = currentEquipmentIds.includes(equipmentId)
      ? currentEquipmentIds.filter((id) => id !== equipmentId)
      : [...currentEquipmentIds, equipmentId]

    onEquipmentIdsChange(equipmentIds)
  }

  return (
    <SlotPill selected={currentEquipmentIds.includes(equipmentId)} onClick={handleSlotClick}>
      {equipment.name}
    </SlotPill>
  )
}

const useSlotsForHour = (hour) => {
  const slots = useSlotsByHour()

  return slots[hour] ?? []
}

const HourRow = ({ hour, includePrice = true }) => {
  const slots = useSlotsForHour(hour)
  const currentTime = useCurrentTime()
  const dateObject = DateTime.fromISO(hour)
  const label = `${dateObject.toLocaleString(DateTime.TIME_24_SIMPLE)} - ${dateObject
    .plus({ hour: 1 })
    .toLocaleString(DateTime.TIME_24_SIMPLE)}`

  const orderId = useOrderId()

  const { start_time_interval: interval } = useAppointmentType()

  const columnWidth = useMemo(() => {
    switch (true) {
      case interval >= 15:
        return 1 / 4
      default:
        return 1 / 6
    }
  }, [interval])

  const { onTimeChange, onPractitionerIdsChange, onRoomIdChange, onEquipmentIdsChange } =
    useAppointmentForm()

  const handleSlotClick = (slot) => {
    const { start_time: startTime, suggested_combinations: combinations = [] } = slot
    onTimeChange(startTime)
    trackEvent(EVENT_APPOINTMENT_SLOT_SELECTED, {
      orderId,
      startTime: DateTime.fromJSDate(startTime).toISO({
        suppressMilliseconds: true,
      }),
    })
    const firstCombination = first(combinations)

    if (firstCombination) {
      onPractitionerIdsChange(firstCombination.practitioner_ids)
      onRoomIdChange(firstCombination.room_id)
    }

    if (slot.equipment_ids && slot.equipment_ids.length > 0) {
      onEquipmentIdsChange(slot.equipment_ids)
    }
  }

  return (
    <CondensedRow mb="md">
      <CondensedCol col={1}>
        <Label fontSize="body3" color="nude.700">
          {label}
        </Label>
      </CondensedCol>
      {slots.map((slot, index) => (
        <CondensedCol col={columnWidth} key={index}>
          <SlotPill
            selected={DateTime.fromJSDate(slot.start_time).toFormat('HH:mm') === currentTime}
            subtle={
              slot.nonoptimal_practitioner_allocation === true && slot.quantity_available > 0
                ? 1
                : 0
            }
            disabled={slot.quantity_available === 0}
            title={slot.no_availability_message}
            onClick={() => handleSlotClick(slot)}
          >
            <>
              {DateTime.fromJSDate(slot.start_time).toLocaleString(DateTime.TIME_24_SIMPLE)}
              {includePrice && (
                <Box>
                  <Money
                    color="inherit"
                    variant="meta2"
                    my="xs"
                    textAlign="center"
                    lineHeight={1.2}
                    as="span"
                    amount={slot.price / 100}
                    currency={slot.currency}
                  />
                </Box>
              )}
            </>
          </SlotPill>
        </CondensedCol>
      ))}
    </CondensedRow>
  )
}

const SlotPill = ({ selected, disabled, subtle, onClick, children, ...props }) => {
  const handleOnClick = (event) => {
    if (disabled) {
      return
    }

    if (typeof onClick === 'function') {
      onClick(event)
    }
  }

  return (
    <StyledSlotPill
      borderRadius="sm"
      selected={selected}
      subtle={subtle}
      disabled={disabled}
      p="sm"
      display="flex"
      alignItems="center"
      onClick={handleOnClick}
      mb="sm"
      transition="fast"
      {...props}
    >
      <Text
        as="div"
        color="inherit"
        variant="body3"
        textAlign="center"
        lineHeight={1.2}
        alignItems="center"
        mx="auto"
      >
        {children}
      </Text>
    </StyledSlotPill>
  )
}

const StyledSlotPill = styled(Box)(
  ({ selected = false, disabled = false, subtle = false }) => css`
    background-color: ${selected
      ? 'info.500'
      : subtle
      ? 'light.500'
      : disabled
      ? 'nude.100'
      : 'info.100'};

    color: ${selected ? 'light.900' : subtle ? 'nude.600' : disabled ? 'nude.600' : 'info.900'};

    &:hover {
      ${disabled
        ? css`
            background-color: nude.600;
          `
        : selected
        ? css`
            background-color: info.200;
          `
        : css`
            background-color: info.200;
            color: light.900;
            border-width: xs;
          `}
    }

    cursor: ${disabled ? 'not-allowed' : 'pointer'};
  `
)

const InvalidPractitionerAlert = () => {
  const currentPractitionerIds = useCurrentPractitionerIds()
  const { practitioner_ids: practitionerIds = [] } = useCurrentSlot()
  const invalidPractitionerIds = currentPractitionerIds.filter(
    (practitionerId) => !practitionerIds.includes(practitionerId)
  )
  const invalidPractitioners = useSelector(selectPractitionersByIds(invalidPractitionerIds)) || []
  const invalidPractitionerNames = map(invalidPractitioners, 'name')

  if (invalidPractitionerIds.length > 0) {
    return (
      <Alert variant="warning" p="sm" fontSize="body2" mb="sm">
        <Box>
          You have selected <strong>{invalidPractitionerNames.join(', ')}</strong>.
        </Box>
        <Text color="inherit" fontSize="body3">
          This practitioner is not valid for this time.{' '}
          {practitionerIds.length > 0 && <>Select from the valid practitioners below.</>}
        </Text>
      </Alert>
    )
  }

  return null
}

const InvalidRoomAlert = () => {
  const currentRoomId = useCurrentRoomId()
  const { room_ids: roomIds = [] } = useCurrentSlot()
  const { data: { data: { name = '' } = {} } = {} } = useGetRoom(currentRoomId)

  if (currentRoomId && !roomIds.includes(currentRoomId)) {
    return (
      <Alert variant="warning" p="sm" fontSize="body2" mb="sm">
        <Box>
          You have selected <strong>{name}</strong>.
        </Box>
        <Text color="inherit" fontSize="body3">
          This room is not valid for this time.
          {roomIds.length > 0 && <>Select from the valid rooms below.</>}
        </Text>
      </Alert>
    )
  }

  return null
}

const CondensedRow = ({ children, ...props }) => {
  return (
    <Row mx="-sm" {...props}>
      {children}
    </Row>
  )
}
const CondensedCol = ({ children, ...props }) => {
  return (
    <Col px="sm" {...props}>
      {children}
    </Col>
  )
}
